import React from "react";
import { motion } from "framer-motion";
import Fixed from "./Fixed";
import Footer1 from "./Footer1";
import smartSystemImage from "../Assets/service1.avif";

const SmartSystems = () => {
  return (
    <div>
      <Fixed />
      <div className="bg-gray-100 text-gray-900 pt-20">
         {/* Hero Section with Background Image */}
         <section
          className="relative w-full h-screen flex items-center justify-center bg-cover bg-center"
          style={{ backgroundImage: `url(${smartSystemImage})` }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-50"></div> {/* Dark overlay */}
          <div className="relative z-10 text-center text-white px-6">
            <motion.h1
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
              className="text-5xl font-extrabold"
            >
              Smart Systems – Innovate. Automate. Elevate.
            </motion.h1>
            <p className="mt-4 text-lg max-w-2xl mx-auto">
              Experience the power of AI-driven automation, enhancing efficiency across industries.
            </p>
          </div>
        </section>



        <section className="container mx-auto px-6 py-16 flex justify-center">
  <motion.div
    initial={{ opacity: 0, y: -20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 1 }}
    className="bg-white shadow-lg rounded-lg p-8 max-w-4xl text-center"
  >
    <h2 className="text-4xl font-bold text-slate-800">About Smart Systems</h2>
    <div className="mt-6 text-lg text-gray-700 leading-relaxed">
      <p>
        Smart systems are revolutionizing the way we interact with technology by bringing intelligence, automation, and convenience to various sectors. These systems leverage artificial intelligence, machine learning, and IoT connectivity to optimize performance, reduce operational costs, and improve efficiency.
      </p>
      <p className="mt-4">
        From smart homes that adjust lighting and temperature automatically to industrial automation that streamlines production, smart systems are at the forefront of modern innovation. With real-time data analytics and seamless remote access, users can experience unparalleled control over their environments.
      </p>
      <p className="mt-4">
        Whether it's enhancing security with advanced monitoring or reducing energy consumption through intelligent automation, smart systems are paving the way for a more connected and sustainable future.
      </p>
    </div>
  </motion.div>
</section>


        {/* Why Choose Us Section */}
        <section className="container mx-auto px-6 py-10">
          <motion.h2
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1 }}
            className="text-4xl font-bold text-center text-slate-800"
          >
            Why Choose Our Smart Systems?
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-10">
            {[
              { title: "AI-Powered Automation", desc: "Harness the power of AI for intelligent control." },
              { title: "Energy Optimization", desc: "Reduce energy wastage with smart monitoring." },
              { title: "Seamless Remote Access", desc: "Control your devices from anywhere, anytime." },
              { title: "Security & Alerts", desc: "Advanced surveillance and real-time alerts." },
              { title: "Integration Flexibility", desc: "Compatible with multiple platforms and devices." },
              { title: "Cost-Effective Solutions", desc: "Affordable automation for homes and industries." },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition border-t-4 border-blue-500"
              >
                <h3 className="text-2xl font-semibold text-blue-600">{item.title}</h3>
                <p className="text-gray-700 mt-2">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Key Features Section */}
        <section className="bg-gray-50 py-16">
          <div className="container mx-auto px-6 text-center">
            <h2 className="text-4xl font-bold text-slate-800">Key Features</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-10">
              {[
                { title: "Home Automation", desc: "Control lighting, climate, and security effortlessly." },
                { title: "Industrial Efficiency", desc: "Optimize production with intelligent monitoring." },
                { title: "Smart Workspaces", desc: "Automate workflow and security in offices." },
                { title: "Voice Control", desc: "Operate devices hands-free with AI assistants." },
                { title: "Real-time Analytics", desc: "Monitor data insights for better decisions." },
                { title: "Eco-Friendly Tech", desc: "Sustainable automation solutions." },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition border-b-4 border-blue-500 text-gray-900"
                >
                  <h3 className="text-2xl font-semibold text-blue-600">{item.title}</h3>
                  <p className="mt-2 text-gray-700">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="container mx-auto px-6 py-16">
          <h2 className="text-4xl font-bold text-center text-slate-800">What Our Clients Say</h2>
          <div className="mt-10 space-y-6 flex flex-col items-center">
            {[
              "Revolutionized our factory automation!",
              "Made our home smarter and energy-efficient!",
              "Unmatched security and convenience!",
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className="bg-white p-6 rounded-lg shadow-md flex items-center justify-center w-full max-w-2xl text-center"
              >
                <span className="text-2xl font-bold text-blue-600">❝</span>
                <p className="text-gray-700 mx-4">{testimonial}</p>
                <span className="text-2xl font-bold text-blue-600">❞</span>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-16 text-center bg-white border-t border-gray-300">
          <div className="container mx-auto px-6">
            <h2 className="text-4xl font-bold text-slate-800">Get Started Today!</h2>
            
          </div>
        </section>
      </div>
      <Footer1 />
    </div>
  );
};

export default SmartSystems;
